import { ReactNode } from 'react';
import { ArrowLeft, LogOut, Moon, MoreVertical, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '@/auth';
import { useTheme } from '@/lib/theme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/features/notifications/NotificationBell';

export type HeaderMenuItem =
  | {
      type: 'checkbox';
      label: string;
      icon?: ReactNode;
      checked: boolean;
      onCheckedChange: (checked: boolean) => void;
    }
  | {
      type: 'action';
      label: string;
      icon?: ReactNode;
      onClick: () => void;
    };

interface HeaderProps {
  title: string;
  subtitle?: ReactNode;
  showBack?: boolean;
  backTo?: string;
  rightContent?: ReactNode;
  menuItems?: HeaderMenuItem[];
  className?: string;
}

export function Header({
  title,
  subtitle,
  showBack = false,
  backTo,
  rightContent,
  menuItems,
  className,
}: HeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };
  const { user, logout } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();

  return (
    <header
      className={cn(
        'sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border safe-area-top',
        className
      )}
    >
      <div className="flex items-center h-[var(--header-height)] px-4 gap-3">
        {/* Left: Back button or spacer */}
        {showBack ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Назад"
            onClick={handleBack}
            className="-ml-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        ) : (
          <div className="w-10" />
        )}

        {/* Center: Title */}
        <div className="flex-1 text-center">
          <h1 className="text-base font-semibold truncate">{title}</h1>
          {subtitle && (
            typeof subtitle === 'string' ? (
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            ) : (
              <div className="text-xs text-muted-foreground">{subtitle}</div>
            )
          )}
        </div>

        {/* Right: Custom content + menu */}
        <div className="flex items-center gap-1">
          <NotificationBell />
          {rightContent}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="-mr-2">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Custom menu items */}
              {menuItems && menuItems.length > 0 && (
                <>
                  {menuItems.map((item, idx) =>
                    item.type === 'checkbox' ? (
                      <DropdownMenuCheckboxItem
                        key={idx}
                        checked={item.checked}
                        onCheckedChange={item.onCheckedChange}
                      >
                        {item.icon}
                        {item.label}
                      </DropdownMenuCheckboxItem>
                    ) : (
                      <DropdownMenuItem key={idx} onClick={item.onClick}>
                        {item.icon}
                        {item.label}
                      </DropdownMenuItem>
                    )
                  )}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem disabled className="text-muted-foreground">
                {user?.name || user?.username}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme}>
                {resolvedTheme === 'light' ? (
                  <>
                    <Moon className="mr-2 h-4 w-4" />
                    Тёмная тема
                  </>
                ) : (
                  <>
                    <Sun className="mr-2 h-4 w-4" />
                    Светлая тема
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Выход
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
